/* Crystal House: live 3D photo crystal on an engraved fine-crystal base.
   Custom WebGL2 SDF renderer. No libraries.
   The photograph's own pixels become the subsurface engraving: luminance drives
   both point density and depth, which is how 3D subsurface laser engraving works. */
(function () {
  "use strict";

  var VERT = "#version 300 es\nin vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";

  var FRAG = "#version 300 es\n" + [
"precision highp float;",
"out vec4 outCol;",
"uniform vec2  uRes;",
"uniform float uTime;",
"uniform vec2  uRot;",      // yaw, pitch
"uniform sampler2D uPhoto;",
"uniform sampler2D uText;",
"uniform float uQuality;",  // 1.0 desktop, 0.0 lite
"uniform vec3  uInk;",      // page background to blend into

// --- geometry constants -----------------------------------------------------
"const vec3 BH = vec3(0.615, 0.880, 0.255);",   // crystal block half extents
"const vec3 SH = vec3(0.800, 0.170, 0.340);",   // base half extents
"const float BY = 1.220;",                       // block centre height
"const float SY = 0.170;",                       // base centre height

"float sdBox(vec3 p, vec3 b){ vec3 q=abs(p)-b; return length(max(q,0.0))+min(max(q.x,max(q.y,q.z)),0.0); }",

// chamfered box: box cut by three 45 degree plane pairs, like a polished crystal edge
"float sdChamfer(vec3 p, vec3 b, float c){",
"  float d = sdBox(p,b);",
"  vec3 q = abs(p);",
"  d = max(d, (q.x+q.y)*0.70710678 - (b.x+b.y)*0.70710678 + c);",
"  d = max(d, (q.y+q.z)*0.70710678 - (b.y+b.z)*0.70710678 + c);",
"  d = max(d, (q.x+q.z)*0.70710678 - (b.x+b.z)*0.70710678 + c);",
"  return d;",
"}",

"float map(vec3 p){",
"  float a = sdChamfer(p - vec3(0.0,BY,0.0), BH, 0.058);",
"  float b = sdChamfer(p - vec3(0.0,SY,0.0), SH, 0.048);",
"  float k = 0.030;",
"  float h = clamp(0.5 + 0.5*(b-a)/k, 0.0, 1.0);",
"  return mix(b, a, h) - k*h*(1.0-h);",
"}",

"vec3 nrm(vec3 p){",
"  vec2 e = vec2(0.0012,0.0);",
"  return normalize(vec3(map(p+e.xyy)-map(p-e.xyy), map(p+e.yxy)-map(p-e.yxy), map(p+e.yyx)-map(p-e.yyx)));",
"}",

"float hash(vec3 v){ return fract(sin(dot(v, vec3(12.9898,78.233,37.719)))*43758.5453); }",

// --- room ------------------------------------------------------------------
"vec3 env(vec3 rd){",
"  vec3 c = mix(vec3(0.026,0.020,0.018), vec3(0.075,0.058,0.046), smoothstep(-0.25,0.85,rd.y));",
"  c += vec3(1.00,0.80,0.48) * 3.4 * pow(max(0.0, dot(rd, normalize(vec3( 0.60, 0.48,-0.62)))), 24.0);",
"  c += vec3(0.52,0.64,0.96) * 1.1 * pow(max(0.0, dot(rd, normalize(vec3(-0.86, 0.30, 0.16)))), 14.0);",
"  c += vec3(1.00,0.60,0.26) * 0.9 * pow(max(0.0, dot(rd, normalize(vec3( 0.44,-0.10, 0.80)))), 28.0);",
"  c += vec3(1.00,0.86,0.62) * 0.6 * pow(max(0.0, dot(rd, normalize(vec3(-0.30, 0.72, 0.60)))), 18.0);",
"  return c;",
"}",

// --- the engraving ---------------------------------------------------------
// Luminance sets how many laser points fire and how deep they sit.
"float engrave(vec3 p, out float warm){",
"  warm = 0.0;",
"  vec3 q = p - vec3(0.0,BY,0.0);",
"  float hw = BH.x*1.24;",
"  vec2 uv = vec2(0.5 - q.x/hw*0.5, 0.5 - (q.y-0.135)/hw*0.5);",
"  if(uv.x<0.0||uv.x>1.0||uv.y<0.0||uv.y>1.0) return 0.0;",
"  vec3 t = texture(uPhoto, uv).rgb;",
"  float lum = dot(t, vec3(0.30,0.59,0.11));",
"  lum = pow(clamp((lum-0.12)/0.80, 0.0, 1.0), 1.35);",
// soft oval vignette so the image floats in the crystal instead of hitting the walls
"  vec2 d2 = (uv-0.5)*vec2(2.00,2.00);",
"  lum *= smoothstep(1.02, 0.42, length(d2));",
"  if(lum < 0.012) return 0.0;",
// depth relief: brighter parts of the photograph sit closer to the viewer
"  float zc = (lum-0.46) * BH.z * 0.62;",
"  float dz = abs(q.z - zc);",
"  float slab = exp(-dz*dz/0.0042);",
// discrete laser points rather than a smooth projection
"  vec3 cell = floor(p*236.0);",
"  float pt = step(1.0 - lum*0.88, hash(cell));",
"  float fine = step(1.0 - lum*0.34, hash(cell*1.93+7.1));",
"  warm = lum;",
"  return (pt*0.85 + fine*0.55) * slab * lum;",
"}",

// base engraving: frosted sand carved text on the front face of the base
"float baseText(vec3 p){",
"  vec3 q = p - vec3(0.0,SY,0.0);",
"  if(q.z < SH.z*0.42) return 0.0;",
"  vec2 uv = vec2(0.5 - q.x/(SH.x*0.80)*0.5, 0.5 - q.y/(SH.y*0.72)*0.5);",
"  if(uv.x<0.0||uv.x>1.0||uv.y<0.0||uv.y>1.0) return 0.0;",
"  float a = texture(uText, uv).a;",
"  float dz = abs(q.z - SH.z*0.66);",
"  return a * exp(-dz*dz/0.0021);",
"}",

// --- polished stone floor ---------------------------------------------------
"vec3 floorCol(vec3 ro, vec3 rd){",
"  float t = -ro.y/rd.y;",
"  vec3 p = ro + rd*t;",
"  float r = length(p.xz);",
"  vec3 refl = env(reflect(rd, vec3(0.0,1.0,0.0)));",
"  vec3 c = vec3(0.021,0.017,0.015) + refl*0.045;",
// warm smear beneath the piece, then a contact shadow that grounds it
"  c += vec3(1.0,0.76,0.42) * 0.10 * exp(-r*r*0.55) * smoothstep(2.2,0.2,abs(p.z));",
"  c *= smoothstep(0.55, 2.10, r) * 0.55 + 0.18;",
"  c *= 1.0 - 0.86*exp(-dot(p.xz,p.xz)*1.45);",
"  return c * smoothstep(14.0, 2.0, t);",
"}",

// --- ACES tone map ----------------------------------------------------------
"vec3 aces(vec3 x){ return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14), 0.0, 1.0); }",

"void main(){",
"  vec2 uv = (gl_FragCoord.xy - 0.5*uRes)/uRes.y;",
"  float cy = cos(uRot.x), sy = sin(uRot.x);",
"  float cp = cos(uRot.y), sp = sin(uRot.y);",
"  vec3 tgt = vec3(0.0, 1.00, 0.0);",
"  float rad = 4.62;",
"  vec3 ro = tgt + vec3(sy*cp, sp, cy*cp)*rad;",
"  vec3 fw = normalize(tgt-ro), rt = normalize(cross(vec3(0.0,1.0,0.0), fw)), up = cross(fw,rt);",
"  vec3 rd = normalize(fw*1.58 + rt*uv.x + up*uv.y);",

"  vec3 col;",
"  float t = 0.0; float d = 1e9; bool hit=false;",
"  int OUTER = uQuality>0.5 ? 96 : 58;",
"  for(int i=0;i<96;i++){",
"    if(i>=OUTER) break;",
"    vec3 p = ro + rd*t;",
"    d = map(p);",
"    if(d < 0.0016){ hit=true; break; }",
"    t += d;",
"    if(t > 12.0) break;",
"  }",

"  if(!hit){",
"    vec3 sky = env(rd)*0.022 + vec3(0.018,0.015,0.013);",
"    vec3 grd = rd.y < -0.0005 ? floorCol(ro,rd) : sky;",
"    col = mix(sky, grd, smoothstep(0.010, -0.055, rd.y));",
"  } else {",
"    vec3 p = ro + rd*t;",
"    vec3 n = nrm(p);",
"    float fres = 0.045 + 0.955*pow(1.0 - max(dot(-rd,n),0.0), 5.0);",
"    vec3 rfl = reflect(rd,n);",
"    vec3 reflCol = rfl.y < -0.001 ? floorCol(p+n*0.004, rfl) : env(rfl);",

"    vec3 ird = refract(rd, n, 1.0/1.552);",
"    vec3 acc = vec3(0.0);",
"    vec3 ip = p + ird*0.006;",
"    float travelled = 0.0;",
"    int INNER = uQuality>0.5 ? 56 : 32;",
"    float stp = uQuality>0.5 ? 0.026 : 0.046;",
"    for(int i=0;i<56;i++){",
"      if(i>=INNER) break;",
"      ip += ird*stp; travelled += stp;",
"      if(map(ip) > 0.0) break;",
"      float warm;",
"      float e = engrave(ip, warm);",
"      if(e > 0.0){",
"        vec3 tint = mix(vec3(1.00,0.93,0.80), vec3(1.00,0.84,0.60), 1.0-warm);",
"        acc += tint * e * stp * 27.0;",
"      }",
"      float bt = baseText(ip);",
"      if(bt > 0.0) acc += vec3(1.00,0.90,0.70) * bt * stp * 28.0;",
"    }",
// exit face, with dispersion: three wavelengths leave at slightly different angles
"    vec3 en = nrm(ip);",
"    vec3 tir = reflect(ird, -en);",
"    vec3 outR = refract(ird, -en, 1.5475); if(dot(outR,outR)<1e-6) outR = tir;",
"    vec3 outG = refract(ird, -en, 1.5520); if(dot(outG,outG)<1e-6) outG = tir;",
"    vec3 outB = refract(ird, -en, 1.5585); if(dot(outB,outB)<1e-6) outB = tir;",
"    vec3 through = min(vec3(env(outR).r, env(outG).g, env(outB).b), vec3(3.0));",
// lead crystal is not perfectly clear: a faint warm absorption over path length
"    through *= exp(-travelled*vec3(0.10,0.14,0.20));",
"    col = mix(through*0.92 + acc, reflCol, fres);",
// total internal reflection along the chamfers reads as a bright drawn edge
"    float edge = pow(1.0 - abs(dot(n, -rd)), 3.0);",
"    col += vec3(1.0,0.90,0.70) * edge * 0.30;",
"  }",

"  col = aces(col*0.92);",
"  col = pow(col, vec3(1.0/2.2));",
// blend the frame into the page ground so the canvas has no visible box
"  float vig = smoothstep(1.24, 0.28, length(uv*vec2(0.62,0.88)));",
"  col = mix(uInk, col, vig);",
"  outCol = vec4(col, 1.0);",
"}"
].join("\n");

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(s));
    }
    return s;
  }

  function textTexture(gl, tex, names, date) {
    var c = document.createElement("canvas");
    c.width = 1024; c.height = 168;
    var x = c.getContext("2d");
    x.clearRect(0, 0, c.width, c.height);
    x.fillStyle = "#fff";
    x.textAlign = "center";
    x.textBaseline = "middle";
    var n = (names || "").toUpperCase().slice(0, 34);
    var d = (date || "").slice(0, 24);
    function track(str, font, y, tr) {
      x.font = font;
      var total = 0, i;
      for (i = 0; i < str.length; i++) total += x.measureText(str[i]).width + tr;
      total -= tr;
      var sx = c.width / 2 - total / 2;
      for (i = 0; i < str.length; i++) {
        var w = x.measureText(str[i]).width;
        x.fillText(str[i], sx + w / 2, y);
        sx += w + tr;
      }
    }
    if (n && d) {
      track(n, '400 54px Fraunces, Georgia, serif', 62, 11);
      track(d, '400 30px Fraunces, Georgia, serif', 120, 7);
    } else if (n) {
      track(n, '400 60px Fraunces, Georgia, serif', 88, 12);
    } else if (d) {
      track(d, '400 46px Fraunces, Georgia, serif', 88, 8);
    }
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  window.CrystalHouseViewer = function (opts) {
    var canvas = opts.canvas, photo = opts.photo, stage = opts.stage;
    var gl = canvas.getContext("webgl2", { antialias: false, alpha: false, powerPreference: "high-performance" });
    if (!gl) { if (stage) stage.setAttribute("data-fallback", "true"); return null; }

    var prog = gl.createProgram();
    try {
      gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    } catch (e) {
      if (stage) stage.setAttribute("data-fallback", "true");
      return null;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var U = {};
    ["uRes", "uTime", "uRot", "uPhoto", "uText", "uQuality", "uInk"].forEach(function (k) {
      U[k] = gl.getUniformLocation(prog, k);
    });

    var photoTex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, photoTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    // A cross-origin photograph without CORS headers cannot become a texture.
    // Rather than fail, hand the section back to its still image.
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, photo);
    } catch (err) {
      if (stage) stage.setAttribute('data-fallback', 'true');
      return null;
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(U.uPhoto, 0);

    var textTex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE1);
    textTexture(gl, textTex, opts.names, opts.date);
    gl.uniform1i(U.uText, 1);

    var ink = opts.ink || [0.133, 0.110, 0.098];
    gl.uniform3f(U.uInk, ink[0], ink[1], ink[2]);

    var lite = window.matchMedia("(max-width: 820px)").matches ||
               window.matchMedia("(pointer: coarse)").matches;
    gl.uniform1f(U.uQuality, lite ? 0.0 : 1.0);

    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var yaw = -0.52, pitch = 0.13, vy = 0, dragging = false, lastX = 0, lastY = 0, moved = false;
    var visible = true, raf = 0, t0 = performance.now();

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, lite ? 1.35 : 1.75);
      var w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    function frame() {
      raf = 0;
      if (!visible) return;
      resize();
      if (!dragging) {
        if (!reduce) yaw += 0.0016 + vy;
        vy *= 0.93;
      }
      pitch = Math.max(-0.45, Math.min(0.62, pitch));
      gl.uniform2f(U.uRes, canvas.width, canvas.height);
      gl.uniform1f(U.uTime, (performance.now() - t0) / 1000);
      gl.uniform2f(U.uRot, yaw, pitch);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!reduce || dragging || Math.abs(vy) > 1e-4) raf = requestAnimationFrame(frame);
    }
    function kick() { if (!raf && visible) raf = requestAnimationFrame(frame); }

    canvas.addEventListener("pointerdown", function (e) {
      dragging = true; moved = false; lastX = e.clientX; lastY = e.clientY;
      canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId);
      if (stage) stage.setAttribute("data-touched", "true");
      kick();
    });
    canvas.addEventListener("pointermove", function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
      yaw -= dx * 0.0062; pitch += dy * 0.0042; vy = -dx * 0.00045;
      kick();
      e.preventDefault();
    });
    ["pointerup", "pointercancel", "pointerleave"].forEach(function (ev) {
      canvas.addEventListener(ev, function () { dragging = false; kick(); });
    });
    canvas.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { yaw += 0.14; kick(); e.preventDefault(); }
      if (e.key === "ArrowRight") { yaw -= 0.14; kick(); e.preventDefault(); }
      if (e.key === "ArrowUp") { pitch -= 0.08; kick(); e.preventDefault(); }
      if (e.key === "ArrowDown") { pitch += 0.08; kick(); e.preventDefault(); }
    });

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
        if (visible) kick();
        else if (raf) { cancelAnimationFrame(raf); raf = 0; }
      }, { threshold: 0.02 }).observe(canvas);
    }
    window.addEventListener("resize", kick, { passive: true });
    kick();

    return {
      setEngraving: function (names, date) {
        gl.activeTexture(gl.TEXTURE1);
        textTexture(gl, textTex, names, date);
        gl.uniform1i(U.uText, 1);
        kick();
      }
    };
  };
})();